import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Popper, ClickAwayListener } from '@material-ui/core'
import classNames from 'classnames'

import { TrackingEvents } from '@utils/tracking'
import { Card } from '@components/layout/Card'
import { Button } from '@components/Button'
import ConfigurePdfDownloadModal from '@components/Modal/ConfigurePdfDownloadModal'
import useModalVisibilityControls from '@hooks/useModalVisibilityControls'
import routes from '@utils/routes'

import useFeatureConfig, { FeatureNames } from '@hooks/useFeatureConfig'
import { DeleteJointPlanModal } from './DeleteJointPlanModal'

export const JointScenarioActions = (props) => {

  const {
    paths: { jointPlanPdfReportPath, jointScenarioPath },
    clients,
    tenant: { tenantSlug },
    usePresentValue
  } = props

  const clientsInfo = clients.map(client => ({
    id: client.id,
    name: client.preferredName,
    path: routes.clientProfilePath({ tenantSlug, clientId: client.id })
  }))
  const { menuAnchorElement, toggleMenu, closeMenu, menuVisible, menuId } = usePlanActionsDropdownMenu()
  const [primaryAction, ...additionalActions] = buildPlanActions({
    jointScenarioPath,
    jointPlanPdfReportPath,
    usePresentValue,
    clientsInfo
  })

  return (
    <Card noBorder className='plan-actions'>
      <div className='dropdown-button'>
        {primaryAction.render({ buttonType: 'primary' })}

        {additionalActions.length > 0 ? (
          <MenuTrigger
            aria-describedby={menuId}
            open={menuVisible}
            onClick={toggleMenu} />) : null}
      </div>

      {additionalActions.length > 0 ? (
        <Fragment>
          <Popper
            className='plan-actions__popover-menu'
            id={menuId}
            open={menuVisible}
            anchorEl={menuAnchorElement}>
            <ClickAwayListener onClickAway={closeMenu}>
              <Card className='plan-actions__popover-menu__content'>
                {additionalActions.map(action => action.render({ buttonType: 'secondary' }))}
              </Card>
            </ClickAwayListener>
          </Popper>

          <style>{`
            .plan-actions__popover-menu {
              top: 0.5rem !important;
            }

            .plan-actions__popover-menu__content {
              display: flex;
              flex-direction: column;
              padding: 1rem;
              gap: 1rem;
            }
          `}
          </style>
        </Fragment>) : null}
    </Card>
  )
}

const buildPlanActions = ({ jointPlanPdfReportPath, jointScenarioPath, usePresentValue, clientsInfo }) => {
  const { isEnabled: deleteJointScenarioEnabled } = useFeatureConfig(FeatureNames.JOINT_PLAN__DELETE)

  let actions = [
    {
      isEnabled: true,
      render: ({ buttonType }) => (
        <DownloadJointPlanPDFReportAction
          key="download-joint-plan-pdf-report-action"
          buttonType={buttonType}
          jointPlanPdfReportPath={jointPlanPdfReportPath}
          usePresentValue={usePresentValue} />
      )
    }
  ]

  // TODO: change this to link to the dashboard instead of the client profile
  clientsInfo.forEach(clientInfo => {
    actions.push({
      isEnabled: true,
      render: ({ buttonType }) => (
        <LinkToClientProfileAction
          key={`joint-plan-client-link-${clientInfo.id}`}
          buttonType={buttonType}
          path={clientInfo.path}
          clientName={clientInfo.name} />
      )
    })
  })

  actions.push({
    isEnabled: deleteJointScenarioEnabled,
    render: () =>
      <DeleteJointPlanAction
        jointScenarioPath={jointScenarioPath}
        key="delete-joint-plan-action"
      />
  })

  return actions.filter(({ isEnabled }) => isEnabled)
}

const LinkToClientProfileAction = ({ buttonType, path, clientName }) => {
  const buttonText = `View ${clientName ? clientName : 'Andrew'}'s Profile`

  return (
    <Button
      {...{ [buttonType]: true }}
      fullWidth
      href={path}>
      {buttonText}
    </Button>
  )
}

const DownloadJointPlanPDFReportAction = ({ buttonType, jointPlanPdfReportPath, usePresentValue }) => {
  const { modalVisible, openModal, closeModal } = useModalVisibilityControls()

  return (
    <Fragment>
      <Button
        {...{ [buttonType]: true }}
        fullWidth
        onClick={openModal}
        trackAs={TrackingEvents.jointPlan.downloadPDF}>
        Download PDF
      </Button>

      <ConfigurePdfDownloadModal
        open={modalVisible}
        onClose={closeModal}
        paths={{
          pdfReportPath: jointPlanPdfReportPath,
          notifyCompliancePath: ''
        }}
        usePresentValue={usePresentValue} />
    </Fragment>
  )
}

const DeleteJointPlanAction = ({ jointScenarioPath }) => {
  const { modalVisible, openModal, closeModal } = useModalVisibilityControls()

  return (
    <Fragment>
      <Button warning onClick={openModal} trackAs={TrackingEvents.jointPlan.delete}>
        Delete joint plan
      </Button>

      {modalVisible
        ?
        <DeleteJointPlanModal
          jointScenarioPath={jointScenarioPath}
          open={modalVisible}
          onClose={closeModal}
        />
        : null
      }
    </Fragment>
  )
}

const usePlanActionsDropdownMenu = () => {
  const [menuAnchorElement, setMenuAnchorElement] = React.useState()
  const toggleMenu = (event) => {
    setMenuAnchorElement(menuAnchorElement ? null : event.currentTarget)
  }
  const closeMenu = (event) => {
    event && event.preventDefault()
    setMenuAnchorElement(null)
  }
  const menuVisible = Boolean(menuAnchorElement)
  const menuId = open ? 'plan-actions-popover-menu' : undefined

  return {
    menuAnchorElement,
    toggleMenu,
    closeMenu,
    menuVisible,
    menuId
  }
}

const MenuTrigger = ({ open, onClick, ...passThroughProps }) => {
  const iconClass = classNames('dropdown-arrow', {
    'dropdown-menu-open': open
  })

  return (
    <Button
      className='button plan-actions__menu-trigger'
      style={{ maxWidth: '3rem' }}
      trackAs='Joint Plan: Dropdown actions'
      onClick={onClick}
      secondary
      {...passThroughProps}>
      <span className={iconClass} />
    </Button>
  )
}

JointScenarioActions.propTypes = {
  userDetails: PropTypes.shape({
    isWealthManager: PropTypes.bool.isRequired
  }).isRequired,
  paths: PropTypes.shape({
    jointPlanPdfReportPath: PropTypes.string.isRequired
  }).isRequired,
  clients: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    preferred_name: PropTypes.string.isRequired
  })).isRequired,
  tenant: PropTypes.shape({
    tenantSlug: PropTypes.string.isRequired
  }).isRequired,
  usePresentValue: PropTypes.bool.isRequired
}
